import { useCallback, useEffect, useMemo, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Grid, Modal, Paper, Tab, Typography } from "@mui/material";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { View } from "../../../components/layout/View";
import { PageType } from "../../../config/page.types";
import { useBreadcrumbs } from "../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../context/PageConfigrations/usePageConfigurationState";
import { NodesMapDTO, NodesMapPayload } from "../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { usePeriodFilter } from "../../../hooks/usePreferences";
import { useReadResource } from "../../../hooks/useResource";
import { usePermissions } from "../../../permissions/usePermissions";
import { MapBounds } from "../../results/ResultsOperationalUnits/useOperationalUnitsData";
import { OperationalUnitsList } from "./tabs/ListOperationalUnits";
import { MapOperationalUnits } from "./tabs/MapOperationalUnits";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 600,
	bgcolor: "background.paper",
	p: 2,
};

export const MasterDataOperationalUnits = () => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	const { appliedFilters } = useFiltersRegistry();
	const filtersPayload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	// controls modal open state
	const [open, setOpen] = useState(false);

	// open modal
	const handleOpen = () => setOpen(true);

	// close modal
	const handleClose = () => setOpen(false);

	// selected period filter
	const { formattedFrom: from, formattedTo: to } = usePeriodFilter();

	// MAP STATE
	const [mapBounds, setMapBounds] = useState<MapBounds | undefined>(undefined);

	// Page configuration, stored in localStorage by PageType
	const [config, setConfig] = usePageConfigurationState(PageType.MasterDataOperationalUnits, {
		tabValue: "list",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
	});

	// switch between list and map view
	const handleTabChange = (_: any, value: "list" | "map") => {
		setConfig({ tabValue: value });
	};

	// build map API payload
	const nodesMapPayload: NodesMapPayload | undefined = useMemo(() => {
		if (!mapBounds) return undefined;
		return {
			...filtersPayload,
			from,
			to,
			min_lat: mapBounds.min_lat,
			min_lon: mapBounds.min_lon,
			max_lat: mapBounds.max_lat,
			max_lon: mapBounds.max_lon,
			zoom: mapBounds.zoom ?? 5,
		};
	}, [from, to, mapBounds, filtersPayload]);

	// fetch map data
	const nodesMap = useReadResource<NodesMapPayload, NodesMapDTO>(
		{ route: ApiRoutes.ResultGreerouterNodesMap },
		{ readMethod: "post", disabled: !nodesMapPayload },
		nodesMapPayload
	);

	/**
	 * Debounced state update for map bounds
	 * - Prevents excessive re-renders and query spam when user moves the map
	 * - 500ms delay after map stops moving
	 */
	const debouncedSetMapBounds = useMemo(
		() =>
			debounce((newBounds: MapBounds) => {
				setMapBounds((prev) => {
					const changed =
						prev?.min_lat !== newBounds.min_lat ||
						prev?.max_lat !== newBounds.max_lat ||
						prev?.min_lon !== newBounds.min_lon ||
						prev?.max_lon !== newBounds.max_lon ||
						prev?.zoom !== newBounds.zoom;
					return changed ? newBounds : prev;
				});
			}, 500),
		[]
	);

	// cleanup debounce on unmount
	useEffect(() => {
		return () => debouncedSetMapBounds.cancel();
	}, [debouncedSetMapBounds]);

	// handle map viewport changes
	const handleBoundsChange = useCallback(
		(bounds: MapBounds) => debouncedSetMapBounds(bounds),
		[debouncedSetMapBounds]
	);

	const { canAccessPage } = usePermissions();

	return (
		<View
			pageType={PageType.MasterDataOperationalUnits}
			headerContext={{
				navigate,
				visitPage,
				openModal: handleOpen,
				isAddForbidden: !canAccessPage(PageType.MasterDataOperationalUnitsAddUnit),
			}}
			sx={{ maxWidth: "100vw", overflowX: "hidden" }}
		>
			<TabContext value={config.tabValue}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<TabList onChange={handleTabChange}>
							<Tab label={t("common:list")} value="list" />
							<Tab label={t("common:map")} value="map" />
						</TabList>
					</Grid>

					<Grid size={12}>
						<TabPanel value="list" sx={{ p: 0 }}>
							<OperationalUnitsList />
						</TabPanel>

						<TabPanel value="map" sx={{ p: 0 }}>
							<MapOperationalUnits
								map={nodesMap}
								onBoundsChange={handleBoundsChange}
								mapBounds={mapBounds}
							/>
						</TabPanel>
					</Grid>
				</Grid>
			</TabContext>

			<Modal open={open} onClose={handleClose}>
				<Paper sx={style}>
					<Typography variant="h6">Import anagrafica unità operativa</Typography>
				</Paper>
			</Modal>
		</View>
	);
};
